import { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getEmployeeInfo,
  getAllPrivileges,
  Privilege,
  getPassStatus,
} from "../../services/EmployeeService";
import { handleAddToWallet as handleAddToWalletService } from "../../services/PassKitService";
import PrivilegeCard from "../PrivilegeCard/PrivilegeCard";
import PrivilegeHeader from "./PrivilegeHeader";
import EmployeeCard from "./EmployeeCard";
import "./PrivilegePage.css";

const PrivilegePage = () => {
  const [privileges, setPrivileges] = useState<Privilege[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [employeeFirstName, setEmployeeFirstName] = useState<string>("");
  const [employeeLastName, setEmployeeLastName] = useState<string>("");
  const [employeeType, setEmployeeType] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [passId, setPassId] = useState<string | undefined>(undefined);
  const [isPassIssued, setIsPassIssued] = useState<boolean>(false);
  const [isPassInstalled, setIsPassInstalled] = useState<boolean>(false);
  const { employeeId } = useParams();

  const fetchEmployeeData = async (employeeId: string) => {
    setIsLoading(true);
    try {
      const employeeInfo = await getEmployeeInfo(employeeId);

      setEmployeeFirstName(employeeInfo.first_name || "");
      setEmployeeLastName(employeeInfo.last_name || "");
      setEmployeeType(employeeInfo.user_type || "");
      setCompanyName(employeeInfo.compagny?.name || "");

      if (employeeInfo.pass_id) {
        setPassId(employeeInfo.pass_id);
        try {
          const statusResponse = await getPassStatus(employeeInfo.pass_id);
          const passMetaDataStatus =
            statusResponse.record?.passMetaData?.status;
          // Pass is considered "issued" if it's PASS_ISSUED or PASS_UNINSTALLED
          // (both mean the pass exists and can be opened with openPassKitUrl)
          setIsPassIssued(
            passMetaDataStatus === "PASS_ISSUED" ||
              passMetaDataStatus === "PASS_UNINSTALLED"
          );
          setIsPassInstalled(passMetaDataStatus === "PASS_INSTALLED");
        } catch {
          setIsPassIssued(false);
          setIsPassInstalled(false);
        }
      } else {
        setPassId(undefined);
        setIsPassIssued(false);
        setIsPassInstalled(false);
      }

      const allPrivileges = await getAllPrivileges(+employeeId);
      setPrivileges(allPrivileges);
    } catch {
      setPrivileges([]);
      setEmployeeFirstName("");
      setEmployeeLastName("");
      setEmployeeType("");
      setCompanyName("");
      setPassId(undefined);
      setIsPassIssued(false);
      setIsPassInstalled(false);
    }
    setIsLoading(false);
  };

  const handleAddToWallet = async () => {
    if (!employeeId) return;

    setIsGenerating(true);
    try {
      await handleAddToWalletService(employeeId, isPassIssued, passId, () =>
        fetchEmployeeData(employeeId)
      );
    } catch {
      alert("Failed to generate pass. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeData(employeeId);
    }
  }, [employeeId]);

  // Loader while fetching data
  if (isLoading) {
    return (
      <div className="container">
        <PrivilegeHeader />
        <div className="loader-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <PrivilegeHeader />

      {/* Wallet GIF or Add to Wallet button */}
      {isPassInstalled ? (
        <img
          src="/mobile-wallet (1).gif"
          alt="Mobile Wallet"
          style={{
            width: "151px",
            height: "151px",
            margin: "20px auto",
            display: "block",
          }}
        />
      ) : (
        <>
          <EmployeeCard
            firstName={employeeFirstName}
            lastName={employeeLastName}
            employeeId={employeeId}
            type={employeeType}
            companyName={companyName}
          />
          <button
            className="add-to-wallet-button"
            onClick={handleAddToWallet}
            disabled={isGenerating}
          >
            <img src="/IconWallet.svg" alt="Wallet" className="wallet-icon" />
            {isGenerating ? "Adding..." : "Add your card to Wallet"}
          </button>
        </>
      )}

      {/* Welcome message below the card or GIF */}
      {employeeFirstName && (
        <div className="welcome-label" style={{ marginTop: "16px" }}>
          Welcome {employeeFirstName},
        </div>
      )}

      {/* Wallet description text - shown based on pass installation status */}
      {isPassInstalled ? (
        <div className="wallet-description-label" style={{ marginTop: "16px" }}>
          Your card is in the wallet!
        </div>
      ) : (
        <div className="wallet-description-label" style={{ marginTop: "16px" }}>
          Easily save your card to your Wallet
          <br />
          for quick access anytime.
        </div>
      )}

      <div className="discover-privileges-label" style={{ marginTop: "24px" }}>
        Discover your privileges
      </div>

      {privileges.length > 0 && (
        <div className="privileges-grid-container">
          {privileges.map((privilege) => (
            <PrivilegeCard key={privilege.id} privilege={privilege} />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(PrivilegePage);
